import type { ProjectLangSvgIcon, ProjectLucideTabIcon, ProjectTabIcon } from "@/types/project";
import { PROJECT_LANG_SVG_IDS } from "@/types/project";
import type { LucideIcon } from "lucide-react";
import {
    Atom,
    Bird,
    Droplet,
    File,
    FileCodeCorner,
    Server,
    ShoppingCart,
    SquareTerminal,
    Table2,
} from "lucide-react";

const LANG_SVG_BASE = "/images/langages";

/** Libellés pour l’attribut `alt` des logos (SEO & accessibilité). */
const LANG_SVG_ALT: Record<ProjectLangSvgIcon, string> = {
    html5: "HTML5",
    nextdotjs: "Next.js",
    supabase: "Supabase",
    ansible: "Ansible",
    github: "GitHub",
    tailwindcss: "Tailwind CSS",
    flutter: "Flutter",
    vercel: "Vercel",
    git: "Git",
    pnpm: "pnpm",
    webflow: "Webflow",
    javascript: "JavaScript",
    swift: "Swift",
    laravel: "Laravel",
    shopify: "Shopify",
    wordpress: "WordPress",
};

function isLangSvgIcon(id: ProjectTabIcon): id is ProjectLangSvgIcon {
    return (PROJECT_LANG_SVG_IDS as readonly string[]).includes(id);
}

const LUCIDE_ICONS: Record<ProjectLucideTabIcon, LucideIcon> = {
    SquareTerminal,
    Atom,
    File,
    Server,
    FileCodeCorner,
    ShoppingCart,
    Droplet,
    Table2,
    Bird,
};

const LUCIDE_COLORS: Partial<Record<ProjectLucideTabIcon, string>> = {
    SquareTerminal: "text-primary",
    Atom: "text-primary",
    File: "text-yellow-500",
    Server: "text-white/70",
    FileCodeCorner: "text-red-500",
    ShoppingCart: "text-shopify",
    Droplet: "text-droplet",
    Table2: "text-cyan-500",
    Bird: "text-orange-500",
};

type ProjectFileIconProps = {
    fileIcon: ProjectTabIcon;
    className?: string;
};

export default function ProjectFileIcon({ fileIcon, className = "size-3.5 shrink-0" }: ProjectFileIconProps) {
    if (isLangSvgIcon(fileIcon)) {
        return (
            <img
                src={`${LANG_SVG_BASE}/${fileIcon}.svg`}
                alt={`Logo ${LANG_SVG_ALT[fileIcon]}`}
                className={`${className} object-contain opacity-90`}
                draggable={false}
            />
        );
    }

    const Icon = LUCIDE_ICONS[fileIcon] ?? File;
    const colorClass = LUCIDE_COLORS[fileIcon] ?? "text-on-surface-variant";
    return <Icon className={`${className} ${colorClass}`} aria-hidden />;
}
